import { useEffect, useRef } from 'react';
import { useNavigationType } from 'react-router-dom';
import { MarketingFooter, MarketingHeader, MARKETING_SITE_URL } from '../marketing/MarketingChrome';
import { getLegalDocument, type LegalDocumentKind } from './legalDocument';
import './LegalPage.css';

/**
 * `/terms` and `/privacy`: the Terms of Service and Privacy Policy, served by the web app so the
 * app, the app stores and signup have public URLs to link to.
 *
 * The text is the marketing site's own page, read at build time (`legalDocument.ts`), so it is
 * never edited here. The page wears rosterpilot.com's header and footer (`MarketingChrome`) and the
 * `--marketing-*` tokens, like `/pricing`. Public and outside every authenticated shell, and it
 * waits on nothing, so it renders the same signed in or out. It is its own shell, so it renders its
 * own skip link and owns the `main#main-content` landmark it targets.
 * No guided tour: tours run for signed-in coaches inside the app, and this page is pre-app.
 */
export const LegalPage = ({ kind }: { kind: LegalDocumentKind }) => {
  const legal = getLegalDocument(kind);
  const navigationType = useNavigationType();
  const mainRef = useRef<HTMLElement>(null);
  const shownKind = useRef(kind);

  // An in-app link here (the footer on /pricing, or this page's own footer) keeps the scroll
  // position of the page it came from, so open the document at its top. Both routes render this
  // component in the same place, so switching between them keeps it mounted: then also move focus
  // to the document, so the next Tab does not land back in the footer. Back, Forward and a fresh
  // load (POP) keep the position the browser restores.
  useEffect(() => {
    const switched = shownKind.current !== kind;
    shownKind.current = kind;
    if (navigationType === 'POP') {
      return;
    }
    window.scrollTo(0, 0);
    if (switched) {
      mainRef.current?.focus({ preventScroll: true });
    }
  }, [kind, navigationType]);

  return (
    <div className="legal-page" data-testid={`legal-page-${kind}`}>
      <a className="mk-skip" href="#main-content">
        Skip to main content
      </a>
      <MarketingHeader />
      <main id="main-content" ref={mainRef} tabIndex={-1} className="legal-main">
        <div className="legal-hero">
          <div className="legal-wrap">
            <nav className="legal-crumb" aria-label="Breadcrumb">
              <ol>
                <li>
                  <a href={MARKETING_SITE_URL}>Home</a>
                </li>
                <li>
                  <span aria-current="page">{legal.title}</span>
                </li>
              </ol>
            </nav>
            <h1 className="legal-hero__title">{legal.title}</h1>
            <p className="legal-hero__updated" data-testid="legal-updated">
              Last updated <time dateTime={legal.updatedIso}>{legal.updatedLabel}</time>
            </p>
          </div>
        </div>
        {/*
          First-party static HTML from marketing/, fixed at build time and cleaned in
          legalDocument.ts. Never user or API content: see the note at the top of that file.
        */}
        <div
          className="legal-wrap legal-body"
          data-testid="legal-body"
          dangerouslySetInnerHTML={{ __html: legal.bodyHtml }}
        />
      </main>
      <MarketingFooter />
    </div>
  );
};
